import { type ReactNode } from 'react';
import { toastStore } from '@components/Toast/toast-store';
import type { ToastAction, ToastType } from '@components/Toast/types';
import { type ToastMessageContext } from './toast-catalog';
import {
  normalizePortugueseText,
  resolveToastMessage,
  toPortugueseFriendlyMessage,
} from './toast-message';

export type { ToastType };

type ToastPosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

type ToastRenderable<Data = unknown> =
  | ReactNode
  | ((payload: { data: Data }) => ReactNode)
  | {
      render?: (payload: { data: Data }) => ReactNode;
    };

export type ToastCompatOptions = {
  type?: ToastType;
  autoClose?: number | false;
  position?: ToastPosition;
  description?: ReactNode;
  id?: string | number;
  duration?: number | false;
  context?: ToastMessageContext;
  [key: string]: unknown;
};

type ToastPromiseMessages<T = unknown> = {
  pending?: ToastRenderable;
  loading?: ToastRenderable;
  success?: ToastRenderable<T>;
  error?: ToastRenderable;
};

type ToastFn = {
  (message: ReactNode, options?: ToastCompatOptions): string;
  success: (message: ReactNode, options?: ToastCompatOptions) => string;
  error: (message: ReactNode, options?: ToastCompatOptions) => string;
  info: (message: ReactNode, options?: ToastCompatOptions) => string;
  warning: (message: ReactNode, options?: ToastCompatOptions) => string;
  errorFrom: (
    error: unknown,
    fallback?: string,
    context?: ToastMessageContext,
    options?: ToastCompatOptions,
  ) => string;
  successMsg: (
    message: ReactNode,
    context?: ToastMessageContext,
    options?: ToastCompatOptions,
  ) => string;
  dismiss: (id?: string | number) => void;
  promise: <T>(
    promise: Promise<T> | (() => Promise<T>),
    messages: ToastPromiseMessages<T>,
    options?: ToastCompatOptions,
  ) => Promise<T>;
};

function resolveDuration(options?: ToastCompatOptions): number | false | undefined {
  if (options?.duration !== undefined) return options.duration;
  if (options?.autoClose !== undefined) return options.autoClose;
  return undefined;
}

function resolveDescription(options?: ToastCompatOptions): string | undefined {
  const desc = options?.description;
  if (typeof desc === 'string') return desc;
  if (typeof desc === 'number') return String(desc);
  return undefined;
}

function resolveRenderable<Data = unknown>(
  renderable: ToastRenderable<Data> | undefined,
  data: Data,
): ReactNode | undefined {
  if (renderable === undefined || renderable === null) return undefined;
  if (typeof renderable === 'function') return renderable({ data });
  if (typeof renderable === 'object' && 'render' in (renderable as object)) {
    return (renderable as { render?: (p: { data: Data }) => ReactNode }).render?.({ data });
  }
  return renderable as ReactNode;
}

function renderableToString(value: ReactNode | undefined): string | undefined {
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return undefined;
}

// ─── Error action (ex.: "Como resolver") ─────────────────────────────────────

/**
 * Quem conhece a solução de um erro (a ajuda) se registra aqui; o serviço de
 * toast não depende de nenhuma tela. Sem resolvedor, o erro sai como sempre.
 */
export type ToastErrorActionResolver = (
  error: unknown,
  context: ToastMessageContext | undefined,
  message: string,
) => ToastAction | undefined;

let errorActionResolver: ToastErrorActionResolver | undefined;

export function registerToastErrorAction(resolver: ToastErrorActionResolver): () => void {
  errorActionResolver = resolver;
  return () => {
    if (errorActionResolver === resolver) errorActionResolver = undefined;
  };
}

function addErrorToast(source: unknown, message: string, context?: ToastMessageContext, options?: ToastCompatOptions): string {
  return toastStore.add({
    type: 'error',
    message,
    description: resolveDescription(options),
    duration: resolveDuration(options),
    action: errorActionResolver?.(source, context, message),
  });
}

// ─── Main function ────────────────────────────────────────────────────────────

const toastBase = ((message: ReactNode, options?: ToastCompatOptions): string => {
  const type: ToastType = (options?.type as ToastType) ?? 'default';
  if (type === 'error') {
    return addErrorToast(message, resolveToastMessage(message, undefined, options?.context), options?.context, options);
  }
  return toastStore.add({
    type,
    message: normalizePortugueseText(String(message ?? '')),
    description: resolveDescription(options),
    duration: resolveDuration(options),
  });
}) as ToastFn;

toastBase.success = (message, options) =>
  toastStore.add({
    type: 'success',
    message: normalizePortugueseText(
      String(message ?? 'Operação concluída com sucesso.'),
    ),
    description: resolveDescription(options),
    duration: resolveDuration(options),
  });

toastBase.error = (message, options) =>
  addErrorToast(message, resolveToastMessage(message, undefined, options?.context), options?.context, options);

toastBase.info = (message, options) =>
  toastStore.add({
    type: 'info',
    message: normalizePortugueseText(String(message ?? '')),
    description: resolveDescription(options),
    duration: resolveDuration(options),
  });

toastBase.warning = (message, options) =>
  toastStore.add({
    type: 'warning',
    message: normalizePortugueseText(String(message ?? '')),
    description: resolveDescription(options),
    duration: resolveDuration(options),
  });

toastBase.errorFrom = (error, fallback, context, options) =>
  addErrorToast(error, resolveToastMessage(error, fallback, context), context, options);

toastBase.successMsg = (message, context, options) =>
  toastStore.add({
    type: 'success',
    message: toPortugueseFriendlyMessage(
      message,
      context,
      'Operação concluída com sucesso',
    ),
    description: resolveDescription(options),
    duration: resolveDuration(options),
  });

toastBase.dismiss = (id?: string | number) => {
  toastStore.dismiss(id !== undefined ? String(id) : undefined);
};

toastBase.promise = async <T>(
  promiseInput: Promise<T> | (() => Promise<T>),
  messages: ToastPromiseMessages<T>,
  options?: ToastCompatOptions,
): Promise<T> => {
  const promise = typeof promiseInput === 'function' ? promiseInput() : promiseInput;
  const context = options?.context;

  const loadingMessage = resolveRenderable(messages.loading ?? messages.pending, undefined);
  const loadingText = renderableToString(loadingMessage);

  const loadingId = loadingText
    ? toastStore.add({
        type: 'loading',
        message: normalizePortugueseText(loadingText),
        duration: false,
      })
    : undefined;

  try {
    const data = await promise;

    if (loadingId) toastStore.dismiss(loadingId);

    const successRenderable = resolveRenderable(messages.success, data);
    const successText = renderableToString(successRenderable);
    if (successRenderable !== undefined) {
      toastStore.add({
        type: 'success',
        message: toPortugueseFriendlyMessage(
          successText ?? successRenderable,
          context,
          'Operação concluída com sucesso',
        ),
        duration: resolveDuration(options),
      });
    }

    return data;
  } catch (error: unknown) {
    if (loadingId) toastStore.dismiss(loadingId);

    const errorRenderable = resolveRenderable(messages.error, error);
    const errorFallback = renderableToString(errorRenderable);
    addErrorToast(error, resolveToastMessage(error, errorFallback, context), context, { duration: resolveDuration(options) });

    throw error;
  }
};

export const toast = toastBase;
